import { Dialog, Transition } from '@headlessui/react';
import cx from 'classnames';
import {
  useRef,
  useEffect,
  FunctionComponent,
  ComponentProps,
  ReactNode,
  Fragment,
  useCallback,
  useState,
} from 'react';

import useOnClickOutside from '../../../hooks/click-outside';
import Button from '../../ui/button';
import IconClose from '../../ui/icons/close';

const CloseButton: FunctionComponent<
  ComponentProps<'button'> & { closeBorderLeft?: boolean }
> = ({ closeBorderLeft = false, ...props }) => (
  <div
    className={cx(
      'absolute right-0 top-0',
      { 'border-l border-neutral-30 lg:border-none': closeBorderLeft },
      'z-200'
    )}
    data-cy="close-slider"
  >
    <Button {...props}>
      <IconClose className={cx('text-neutral-80', 'w-4')} />
    </Button>
  </div>
);

const leftProps = {
  enter: 'duration-500 ease-out',
  enterFrom: '-translate-x-full before:opacity-0',
  enterTo: 'translate-x-0 before:opacity-100',
  leave: 'duration-500 ease-out',
  leaveFrom: 'translate-x-0 before:opacity-100',
  leaveTo: '-translate-x-full before:opacity-0',
};

const rightProps = {
  enter: 'duration-500 ease-out',
  enterFrom: 'translate-x-full before:opacity-0',
  enterTo: 'translate-x-0 before:opacity-100',
  leave: 'duration-500 ease-out',
  leaveFrom: 'translate-x-0 before:opacity-100',
  leaveTo: 'translate-x-full before:opacity-0',
};

const Slider: FunctionComponent<{
  isOpen: boolean;
  children: ReactNode;
  enterFrom?: 'left' | 'right';
  isFullscreenMobile?: boolean;
  onClose: () => void;
  closeBorderLeft?: boolean;
  closeButtonAvailable?: boolean;
  classes?: any;
}> = ({
  children,
  enterFrom = 'left',
  isFullscreenMobile,
  isOpen,
  onClose,
  closeBorderLeft = false,
  closeButtonAvailable = true,
  classes,
}) => {
  const animation = enterFrom === 'left' ? leftProps : rightProps;
  const ref = useRef(null);

  //useOnClickOutside(ref, onClose);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen]);

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" onClose={onClose}>
        {/* Backdrop Fade */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div 
            className="fixed inset-0 bg-[#2b2b2b68] cursor-pointer" 
            aria-hidden="true" 
            onClick={onClose} 
          />
        </Transition.Child>

        {/* Sliding Panel */}
        <div className="fixed inset-0 pointer-events-none">
          <Transition.Child
            as={Fragment}
            enterFrom={enterFrom === 'left' ? '-translate-x-full' : 'translate-x-full'}
            enterTo="translate-x-0"
            leaveFrom="translate-x-0"
            leaveTo={enterFrom === 'left' ? '-translate-x-full' : 'translate-x-full'}
          >
            <Dialog.Panel
              className={cx(
                'pointer-events-auto absolute h-full top-0 bg-white shadow-md will-change-transform transition-transform ease-out duration-500',
                isFullscreenMobile ? 'w-full md:max-w-sm' : 'w-[calc(100vw-3rem)] md:max-w-sm',
                enterFrom === 'left' ? 'left-0' : 'right-0'
              )}
            >
              {closeButtonAvailable && (
                <CloseButton
                  closeBorderLeft={closeBorderLeft}
                  tabIndex={isOpen ? 0 : -1}
                  onClick={onClose}
                />
              )}
              
              <div
                ref={ref}
                className={cx(
                  'relative bg-white z-10 max-h-[100vh] overflow-auto h-full w-full'
                )}
                style={classes}
              >
                {children}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Slider;
